<script setup>
/* global axios */
// [AstraChat] Módulo de Vendas — página "Cobranças" (subitem de Configurações) que o
// cliente vê na empresa. Mostra o plano, o status e as cobranças, com pagamento.
// Usa o `axios` GLOBAL (configurado com auth pelo APIHelper), NÃO `import axios` (cru/sem headers).
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';

const route = useRoute();
const { t } = useI18n();

const loading = ref(true);
const subscription = ref(null);
const charges = ref([]);
const openId = ref(null);
const copiedId = ref(null);

const accountId = computed(() => route.params.accountId);

const statusTone = {
  active: 'teal',
  trialing: 'blue',
  paid: 'teal',
  pending: 'slate',
  overdue: 'amber',
  past_due: 'amber',
  canceled: 'ruby',
  expired: 'ruby',
  refunded: 'blue',
  failed: 'ruby',
};

const statusLabel = s => t(`COBRANCAS.STATUS.${String(s || '').toUpperCase()}`, s || '—');

const toneClass = tone =>
  ({
    teal: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    blue: 'bg-n-blue-3 text-n-blue-11 border-n-blue-6',
    amber: 'bg-n-amber-3 text-n-amber-11 border-n-amber-6',
    ruby: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
    slate: 'bg-n-slate-3 text-n-slate-11 border-n-slate-6',
  })[tone] || 'bg-n-slate-3 text-n-slate-11 border-n-slate-6';

const money = cents =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (Number(cents) || 0) / 100
  );

const fmtDate = d => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');

const methodLabel = m => (m ? t(`COBRANCAS.METHOD.${m.toUpperCase()}`, m) : '—');

const isPayable = c => ['pending', 'overdue'].includes(c.status);

const fetchData = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/cobrancas`
    );
    subscription.value = data.subscription;
    charges.value = data.data || [];
  } catch (e) {
    subscription.value = null;
    charges.value = [];
  } finally {
    loading.value = false;
  }
};

const togglePay = c => {
  openId.value = openId.value === c.id ? null : c.id;
};

const copyPix = async c => {
  try {
    await navigator.clipboard.writeText(c.pix_payload || '');
    copiedId.value = c.id;
    setTimeout(() => {
      if (copiedId.value === c.id) copiedId.value = null;
    }, 2000);
  } catch (e) {
    // clipboard indisponível: o usuário seleciona manualmente
  }
};

onMounted(fetchData);
</script>

<template>
  <SettingsLayout :is-loading="loading" :loading-message="t('COBRANCAS.LOADING')">
    <template #header>
      <BaseSettingsHeader
        :title="t('COBRANCAS.TITLE')"
        :description="t('COBRANCAS.SUBTITLE')"
      />
    </template>
    <template #body>
      <div class="flex flex-col w-full gap-6 mt-4">
        <!-- Resumo do plano -->
        <section
          v-if="subscription"
          class="p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div class="text-label-small uppercase tracking-wide text-n-slate-10">
                {{ t('COBRANCAS.PLAN') }}
              </div>
              <div class="text-heading-3 text-n-slate-12">
                {{ subscription.plan_name }}
              </div>
              <div class="mt-1 text-body-main text-n-slate-11">
                {{ money(subscription.amount_cents) }}
                <span v-if="subscription.billing_type === 'recurring'">
                  / {{ subscription.cycle === 'yearly' ? t('COBRANCAS.YEAR') : t('COBRANCAS.MONTH') }}
                </span>
              </div>
            </div>
            <span
              class="inline-flex items-center h-6 px-2 text-label-small border rounded-md"
              :class="toneClass(statusTone[subscription.status])"
            >
              {{ statusLabel(subscription.status) }}
            </span>
          </div>
          <div
            v-if="subscription.current_period_end || subscription.trial_ends_at"
            class="mt-3 text-body-main text-n-slate-11"
          >
            <template v-if="subscription.status === 'trialing' && subscription.trial_ends_at">
              {{ t('COBRANCAS.TRIAL_UNTIL') }} {{ fmtDate(subscription.trial_ends_at) }}
            </template>
            <template v-else-if="subscription.current_period_end">
              {{ t('COBRANCAS.NEXT_DUE') }} {{ fmtDate(subscription.current_period_end) }}
            </template>
          </div>
        </section>

        <!-- Lista de cobranças -->
        <section class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1">
          <div class="px-5 py-3 border-b border-n-weak text-label font-medium text-n-slate-12">
            {{ t('COBRANCAS.INVOICES') }}
          </div>

          <div v-if="!charges.length" class="p-8 text-center text-body-main text-n-slate-10">
            {{ t('COBRANCAS.EMPTY') }}
          </div>

          <ul v-else class="divide-y divide-n-weak">
            <li v-for="c in charges" :key="c.id" class="px-5 py-4">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div class="flex items-center gap-3">
                  <span
                    class="inline-flex items-center h-6 px-2 text-label-small border rounded-md"
                    :class="toneClass(statusTone[c.status])"
                  >
                    {{ statusLabel(c.status) }}
                  </span>
                  <div>
                    <div class="tabular-nums text-body-main font-medium text-n-slate-12">
                      {{ money(c.amount_cents) }}
                    </div>
                    <div class="text-label-small text-n-slate-10">
                      {{ methodLabel(c.payment_method) }} ·
                      {{ c.status === 'paid' ? t('COBRANCAS.PAID_ON') + ' ' + fmtDate(c.paid_at) : t('COBRANCAS.DUE') + ' ' + fmtDate(c.due_date) }}
                    </div>
                  </div>
                </div>
                <button
                  v-if="isPayable(c)"
                  type="button"
                  class="reset-base inline-flex items-center h-9 px-3.5 text-button text-white rounded-lg bg-n-brand hover:bg-n-blue-10 cursor-pointer"
                  @click="togglePay(c)"
                >
                  {{ openId === c.id ? t('COBRANCAS.CLOSE') : t('COBRANCAS.PAY') }}
                </button>
              </div>

              <!-- Painel de pagamento -->
              <div
                v-if="openId === c.id"
                class="flex flex-col gap-3 p-4 mt-4 border rounded-lg border-n-weak bg-n-alpha-1"
              >
                <div v-if="c.pix_payload">
                  <div class="mb-1 text-label font-medium text-n-slate-12">
                    {{ t('COBRANCAS.PIX_TITLE') }}
                  </div>
                  <div class="flex gap-2">
                    <input
                      :value="c.pix_payload"
                      readonly
                      class="reset-base flex-1 min-w-0 px-3 py-2 font-mono text-xs border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12 outline-none"
                    />
                    <button
                      type="button"
                      class="reset-base inline-flex items-center h-9 px-3 text-button rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer whitespace-nowrap"
                      @click="copyPix(c)"
                    >
                      {{ copiedId === c.id ? t('COBRANCAS.COPIED') : t('COBRANCAS.COPY') }}
                    </button>
                  </div>
                </div>

                <div class="flex flex-wrap gap-2">
                  <a
                    v-if="c.boleto_url"
                    :href="c.boleto_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center h-9 px-3.5 text-button rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    {{ t('COBRANCAS.BOLETO') }}
                  </a>
                  <a
                    v-if="c.invoice_url"
                    :href="c.invoice_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center h-9 px-3.5 text-button rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    {{ t('COBRANCAS.OPEN_INVOICE') }}
                  </a>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
